import { Auth0Context, Auth0Provider } from '@auth0/auth0-react';
import { ChakraProvider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { ReactNode, useCallback } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import AuthenticationProvider from '~auth/AuthenticationProvider';
import { AuthorizationProvider } from '~auth/AuthorizationProvider';
import { useAuthentication } from '~auth/useAuthentication';
import { AppError } from '~components/ui/AppError';
import { PageLoader } from '~components/ui/PageLoader';
import { theme } from '~components/ui/styles/theme';
import ApolloProvider from '~graphql/ApolloProvider';
import { AnalyticsProvider } from '~utils/analytics';

interface Props {
  children: ReactNode;
}

/**
 * Sits right after authentication and before anything that queries org-scoped data
 * (AuthorizationProvider's `me`, App's `organization`) — both require Permission.OrganizationView,
 * which a user with no organization yet won't have. Redirects such a user to the registration
 * portal instead of letting those queries fire and error.
 */
function OrgGate({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    user: { organizationId, email, givenName, familyName },
  } = useAuthentication();

  // organizationId is undefined until the token has been decoded at least once.
  if (isAuthenticated && organizationId === undefined) {
    return <PageLoader />;
  }

  if (isAuthenticated && organizationId === null) {
    const url = new URL(process.env.REACT_APP_REGISTRATION_PORTAL_URL ?? '');
    if (email) url.searchParams.set('email', email);
    if (givenName) url.searchParams.set('givenName', givenName);
    if (familyName) url.searchParams.set('familyName', familyName);
    window.location.href = url.toString();
    return <PageLoader />;
  }

  return <>{children}</>;
}

function AppProvider({ children }: Props) {
  const navigate = useNavigate();

  const onRedirectCallback = useCallback(
    (appState?: Record<string, string>) => {
      if (!appState?.returnTo) return;
      navigate(appState.returnTo, { replace: true });
    },
    [navigate],
  );

  return (
    <ChakraProvider resetCSS theme={theme}>
      <HelmetProvider>
        <AuthenticationProvider
          provider={Auth0Provider}
          providerProps={{
            domain: process.env.REACT_APP_AUTH0_DOMAIN,
            clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              redirect_uri: window.location.origin,
              scope: process.env.REACT_APP_AUTH0_SCOPE,
              useRefreshTokens: true,
              cacheLocation: 'memory',
            },
            onRedirectCallback,
          }}
          context={Auth0Context}
        >
          <ApolloProvider uri={process.env.REACT_APP_API_URI}>
            <OrgGate>
              <AuthorizationProvider>
                <AnalyticsProvider>
                  <Sentry.ErrorBoundary
                    fallback={({ resetError }) => <AppError resetError={resetError} />}
                  >
                    {children}
                  </Sentry.ErrorBoundary>
                </AnalyticsProvider>
              </AuthorizationProvider>
            </OrgGate>
          </ApolloProvider>
        </AuthenticationProvider>
      </HelmetProvider>
    </ChakraProvider>
  );
}

export default AppProvider;
